import { IOptimizeImageConfig, IOptimizeImageOptions } from "./types";

export const CACHE_HIT_DIMENSION_MULTIPLE = 360;

let defaultDPR: number = 1;

/**
 * Sets the DPR for a session. Should only be set once per repo.
 *
 * If you want to set the DPR for a specific image see [[IOptimizeImageOptions.dprOverride]]
 */
export const setImageDPR = (dpr?: number): void => {
  defaultDPR = dpr ? Math.round(dpr * 2) / 2 : 1;
};

export function getDpr(options: IOptimizeImageConfig, isSSR: boolean): number {
  if (options.dprOverride != null && options.dprOverride > 0) {
    return options.dprOverride;
  } else if (isSSR) {
    return 1;
  } else {
    return defaultDPR;
  }
}

function multiplyDprAndRoundUp(value: number, dpr: number): number {
  const TINY_IMAGE_SIZE = 60;
  if (value * dpr <= TINY_IMAGE_SIZE) {
    /*
     * Return a tiny image size to support incremental loading (ie. displaying
     * a low-resolution image while the full image is downloading).
     */
    return TINY_IMAGE_SIZE;
  }

  dpr = Math.min(3, Math.max(1, dpr));
  return Math.round(
    Math.ceil((value * dpr) / CACHE_HIT_DIMENSION_MULTIPLE) *
      CACHE_HIT_DIMENSION_MULTIPLE
  );
}

/**
 * Rounds a value up to the next multiple of 360 for swatch images.
 * Unlike multiplyDprAndRoundUp, this always rounds to 360+ (no tiny image exception).
 */
export function roundUpToMultipleOf360(value: number): number {
  return (
    Math.ceil(value / CACHE_HIT_DIMENSION_MULTIPLE) *
    CACHE_HIT_DIMENSION_MULTIPLE
  );
}

export const optimizeDimensionsForCacheHits = (
  { width, height }: { width?: number; height?: number },
  dpr: number
): { width?: number; height?: number } => {
  if (width && height) {
    const ratio = height / width;
    const optimizedWidth = multiplyDprAndRoundUp(width, dpr);
    const optimizedHeight = Math.round(optimizedWidth * ratio);
    return {
      width: optimizedWidth,
      height: optimizedHeight,
    };
  } else if (width) {
    return { width: multiplyDprAndRoundUp(width, dpr) };
  } else if (height) {
    return { height: multiplyDprAndRoundUp(height, dpr) };
  }
  return {};
};

export function transformOptionsForCacheHits(
  options: IOptimizeImageOptions,
  isSSR: boolean
): IOptimizeImageOptions {
  const dpr = getDpr(options, isSSR);

  return {
    ...options,
    ...optimizeDimensionsForCacheHits(options, dpr),
    dprOverride: 1,
  };
}
